import { useEffect, useState, type FormEvent } from "react";
import type { LocationRow } from "@garden/types/db";
import { supabase } from "../auth";
import { createLocation } from "../db/locations";
import "./LocationCreateModal.css";

interface LocationCreateModalProps {
  gardenId: string;
  onClose: () => void;
  onCreated: (loc: LocationRow) => void;
}

export function LocationCreateModal({ gardenId, onClose, onCreated }: LocationCreateModalProps) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"bed" | "container">("bed");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!supabase) {
      setError("Supabase not configured.");
      return;
    }
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const loc = await createLocation(supabase, {
        garden_id: gardenId,
        name: name.trim(),
        kind,
        notes: notes.trim() || null,
      });
      onCreated(loc);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create location.");
      setSubmitting(false);
    }
  };

  return (
    <div className="Modal__backdrop" onClick={onClose} role="presentation">
      <div
        className="Modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="loc-modal-title"
      >
        <h2 id="loc-modal-title" className="Modal__title">
          New location
        </h2>
        <p className="Modal__subtitle">
          A bed or container in your garden. Polygon and pin position come in
          stage 04 (the map).
        </p>

        <form onSubmit={handleSubmit} className="Modal__form">
          <label className="Modal__field">
            <span>Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              placeholder="North bed"
            />
          </label>

          <label className="Modal__field">
            <span>Kind</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as "bed" | "container")}>
              <option value="bed">Bed</option>
              <option value="container">Container</option>
            </select>
          </label>

          <label className="Modal__field">
            <span>Notes <em>optional</em></span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Sun, soil, anything you want to remember…"
            />
          </label>

          {error && <p className="Modal__error">{error}</p>}

          <div className="Modal__actions">
            <button
              type="button"
              className="PlantForm__btn PlantForm__btn--ghost"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="PlantForm__btn PlantForm__btn--primary"
              disabled={submitting}
            >
              {submitting ? "Creating…" : "Create location"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
